// Considered: AspectSummaryMolecule — rejected because it summarises an AspectDefinition
// for a marketplace card (family/metric/value-policy tags); this renders a sealed
// definition readout for an already-composed AspectRef, with an expandable sheet the
// card has no concept of. `Source: type-composer.jsx:1195-1275` (the `.t-rows-tight`
// `.t-row-asp` row). See spec Design System Reuse Audit. Composes `TypeFieldRowMolecule`
// for the row rather than re-laying-out label/meta. The row is ONE line — tile, name,
// description, then the provenance strip — the way `studio.css:526` makes it
// (`flex-wrap: nowrap`); what the definition holds is the sheet's to say, not the row's.
import React, { useCallback, useRef, useState } from 'react';
import TypeFieldRowMolecule from './TypeFieldRowMolecule';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import GlyphAtom from '../../atoms/Glyph/GlyphAtom';
import './AspectDefSummaryMolecule.scss';

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * One `r.needs` entry (`compose.x`/`compose.n-answer`/`compose.window-at-word-n-at`
 * — the prototype's `t-need` chip). `atWord` is the caller-resolved display word
 * (the prototype's `AT_WORD[n.at]`, e.g. "type" / "per sphere") — this molecule
 * renders, it does not localize.
 */
export interface AspectDefSummaryNeed {
  key: string;
  answer: string;
  atWord: string;
}

/**
 * The row's provenance and settlement strip (contract container
 * `compose > list > each(composed) > list-item > group`, order 0–8) plus the two
 * "nothing to answer" readings that live with the needs chips. Every field is a
 * resolved DISPLAY string: this molecule renders, it does not localize and it
 * does not derive.
 *
 * Contract leaves this strip deliberately does NOT carry yet — `compose.unreviewed`,
 * `compose.drafted-here` and `compose.wired-to` — each needs the per-attachment config
 * store (review confirmation, whether the aspect was drafted here, arc wiring), which
 * nothing writes yet. `compose.r-desc`, `compose.installed-from` and
 * `compose.not-defined-yet-blocks-publish` DO land here: they come from the aspect
 * catalog row the caller joins onto each `AspectRef` (EAINT-897 Phase 2 slice 11).
 */
export interface AspectDefSummaryMeta {
  /** `compose.was` — the aspect's own system name, said when the attachment renamed it. */
  was?: string;
  /**
   * `compose.r-desc` — the aspect definition's own one-line description, from its
   * catalog row. A title, not a paragraph: a composition of six aspects should read as
   * six lines. Absent when the definition carries none — no leaf at all, not an empty one.
   */
  desc?: string;
  /** `compose.r-props` — what this attachment contributes, e.g. "3 properties". */
  properties?: string;
  /** `compose.reconfigured` — an INHERITED attachment this type has overridden. */
  reconfigured?: string;
  /** The `title` on `compose.reconfigured` — what "reconfigured" means, said in full. */
  reconfiguredTitle?: string;
  /**
   * `compose.installed-from` — the aspect arrived through a marketplace listing rather
   * than being authored in this workspace (its catalog row carries a `template_id`).
   */
  installedFrom?: string;
  /**
   * `compose.not-defined-yet-blocks-publish` — the workspace registry (caller org ∪ the
   * platform tenant, `GET /aspects/list`) has no definition for this attachment's system
   * name, so the type cannot publish. Only ever set once the catalog has actually
   * loaded: "not fetched yet" is not "not defined".
   */
  unresolved?: string;
  /** `compose.from` — the ancestor an inherited attachment arrived through, e.g. "from Party". */
  inheritedFrom?: string;
  /** `compose.r-version` — an OWN attachment's version line ("pinned to v2" / "follows the aspect"). */
  version?: string;
  /**
   * `compose.unreviewed` — the attachment's settlement mark. An attachment is not done
   * when the aspect lands, it is done when the answers only this site can give have been
   * given: `open` names them ("2 to set"), `blocking` says whether any are still open,
   * and `title` lists them. Absent when the attachment is settled — an inherited one is
   * only ever marked while it has open answers, since its settlement is the ancestor's.
   */
  review?: { label: string; title: string; blocking: boolean };
  /**
   * `compose.drafted-here` — the aspect's definition was authored from this composer
   * rather than picked out of the catalog (`AspectRef.drafted`).
   */
  draftedHere?: string;
  /** `compose.just-attached` — attached in this editing session, not yet published. */
  justAttached?: string;
  /** `compose.need` — how many needs this attachment declares, e.g. "2 needs". */
  needCount?: string;
  /**
   * `compose.targets`/`compose.seeded-here`/`compose.per-sphere` — where an
   * evaluated aspect's targets come from, and whether that is settled on the type
   * or deferred to each sphere. Only an aspect carrying `evaluation` has one.
   */
  targets?: { label: string; value: string; atWord: string };
  /** `compose.answered-by-the-family` — no needs of its own, because an ancestor answered them. */
  answeredByFamily?: string;
  /** `compose.no-needs` — nothing to answer, and nothing inherited that did. */
  noNeeds?: string;
  /**
   * `compose.wired-to` — the arcs this attachment is wired into, e.g. "wired to Renewal".
   * Only the arcs carrying a stage role other than `none`: an arc listed with every stage
   * set to "none" is an arc the attachment is deliberately NOT wired into.
   */
  wiredTo?: string;
}

export interface AspectDefSummaryMoleculeProps {
  label: string;
  expandLabel: string;
  collapseLabel: string;
  /** @see AspectDefSummaryMeta — the row's provenance / version / settlement strip. */
  meta?: AspectDefSummaryMeta;
  /** The expandable sheet's content — the aspect's contributed properties. */
  children?: React.ReactNode;
  trailing?: React.ReactNode;
  className?: string;
  'data-testid'?: string;
  /** Contract datum for the row's glyph tile (`compose.tile`) — the letter/icon beside the name. */
  glyphDatum?: string;
  /** Contract datum for the row's own name leaf (`compose.cfg-label`), distinct from `label` used for a11y. */
  labelDatum?: string;
  /**
   * The aspect's resolved needs (EAINT-897 C4, `GET /schemas/:id/needs`) — each
   * a `key: answer [atWord]` chip (`compose.x`/`compose.n-answer`/`compose.window-at-word-n-at`).
   * Omitted/empty renders nothing, exactly like the prototype's `r.needs.length === 0`.
   * The chips are in the DOM but not on screen (`studio.css:534`): the row carries the
   * `compose.need` count whose title lists them, and the attachment sheet spells them out.
   */
  needs?: AspectDefSummaryNeed[];
}

const AspectDefSummaryMolecule: React.FC<AspectDefSummaryMoleculeProps> = ({
  label,
  meta,
  expandLabel,
  collapseLabel,
  children,
  trailing,
  className = '',
  'data-testid': dataTestId,
  glyphDatum,
  labelDatum,
  needs,
}) => {
  const [expanded, setExpanded] = useState(false);
  const rootClasses = ['aspect-def-summary', className].filter(Boolean).join(' ');
  // ButtonAtom forwards a PrimeReact `Button` instance, not an HTMLButtonElement — wrap it so
  // focus-return can query the real DOM node instead of casting the ref.
  const toggleWrapperRef = useRef<HTMLSpanElement>(null);
  const detailRef = useRef<HTMLDivElement>(null);

  const close = useCallback(() => {
    setExpanded(false);
    toggleWrapperRef.current
      ?.querySelector<HTMLButtonElement>('[data-testid="aspect-def-summary-toggle"]')
      ?.focus();
  }, []);

  /**
   * The definition sheet traps focus and returns it to the trigger like a modal
   * (spec keyboard map), even though it renders inline rather than as an
   * overlay — the sheet's content is the only tab stop while it is open.
   */
  const handleDetailKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        close();
        return;
      }
      if (e.key !== 'Tab' || !detailRef.current) return;

      const focusable = Array.from(detailRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;

      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    },
    [close]
  );

  const hasNeeds = !!needs && needs.length > 0;

  /**
   * The strip sits in the header row itself, in the contract's order: what the
   * attachment brings, then how it got here, then what is still unsettled.
   */
  const metaChips: React.ReactNode[] = [];
  if (meta?.properties) {
    metaChips.push(
      <span key="props" className="aspect-def-summary__meta-text" data-datum="compose.r-props" data-kind="text">
        {meta.properties}
      </span>
    );
  }
  /*
    The way into the DEFINITION (`compose.button`, `type-composer.jsx:1213`): the
    prototype's row button opens the aspect composer over the type; here it opens the
    sealed readout under the row, which is the definition this composer can show. It
    sits where the contract puts it — after what the attachment brings, before how it
    got here — not at the row's end, and it is the eye the prototype draws for a
    definition that is read rather than edited. The attachment sheet's own way in is
    the caller's (`trailing`): that is the prototype's clickable row, not this button.
  */
  metaChips.push(
    <ButtonAtom
      key="definition"
      variant="text"
      shape="icon-square"
      size="small"
      icon="pi pi-eye"
      aria-label={expanded ? collapseLabel : expandLabel}
      aria-expanded={expanded}
      onClick={() => setExpanded((value) => !value)}
      className="aspect-def-summary__definition-toggle"
      data-datum="compose.button"
      data-kind="button"
      data-testid="aspect-def-summary-toggle"
    />
  );
  if (meta?.reconfigured) {
    metaChips.push(
      <span
        key="reconfigured"
        className="aspect-def-summary__meta-chip"
        data-datum="compose.reconfigured"
        data-kind="chip"
        title={meta.reconfiguredTitle}
      >
        <i className="pi pi-sliders-h" aria-hidden="true" />
        {meta.reconfigured}
      </span>
    );
  }
  if (meta?.inheritedFrom) {
    metaChips.push(
      <span key="from" className="aspect-def-summary__meta-inherit" data-datum="compose.from" data-kind="note">
        <i className="pi pi-lock" aria-hidden="true" />
        {meta.inheritedFrom}
      </span>
    );
  }
  if (meta?.version) {
    metaChips.push(
      <span key="version" className="aspect-def-summary__meta-version" data-datum="compose.r-version" data-kind="chip">
        {meta.version}
      </span>
    );
  }
  // Contract order 4 — after the version line, before `drafted-here`.
  if (meta?.review) {
    metaChips.push(
      <span
        key="review"
        className="aspect-def-summary__meta-review"
        data-datum="compose.unreviewed"
        data-kind="chip"
        data-blocking={meta.review.blocking ? '1' : undefined}
        title={meta.review.title}
      >
        <i aria-hidden="true" />
        {meta.review.label}
      </span>
    );
  }
  // Contract order 5 — between the review mark and `just-attached`.
  if (meta?.draftedHere) {
    metaChips.push(
      <span key="drafted-here" className="aspect-def-summary__meta-chip" data-datum="compose.drafted-here" data-kind="chip">
        {meta.draftedHere}
      </span>
    );
  }
  if (meta?.justAttached) {
    metaChips.push(
      <span key="just-added" className="aspect-def-summary__meta-chip" data-datum="compose.just-attached" data-kind="chip">
        {meta.justAttached}
      </span>
    );
  }
  // Contract order 7 — after `just-attached`, before the need count.
  if (meta?.installedFrom) {
    metaChips.push(
      <span key="installed-from" className="aspect-def-summary__meta-chip" data-datum="compose.installed-from" data-kind="chip">
        <i className="pi pi-shopping-bag" aria-hidden="true" />
        {meta.installedFrom}
      </span>
    );
  }
  if (meta?.needCount) {
    metaChips.push(
      <span key="need-count" className="aspect-def-summary__meta-count" data-datum="compose.need" data-kind="count">
        <i className="pi pi-key" aria-hidden="true" />
        {meta.needCount}
      </span>
    );
  }

  return (
    <div className={rootClasses} data-testid={dataTestId}>
      <TypeFieldRowMolecule
        className="aspect-def-summary__header"
        wrap="nowrap"
        label={label}
        labelDatum={labelDatum}
        leading={
          <GlyphAtom
            variant="tile"
            tileSize="sm"
            tileTreatment="fixed"
            fallbackText={label}
            aria-hidden
            data-datum={glyphDatum}
            data-kind={glyphDatum ? 'tile' : undefined}
            data-letter-datum={glyphDatum ? 'compose.g-text' : undefined}
          />
        }
        afterLabel={
          meta?.was || meta?.desc ? (
            <>
              {meta?.was && (
                <span className="aspect-def-summary__was" data-datum="compose.was" data-kind="hint">
                  {meta.was}
                </span>
              )}
              {meta?.desc && (
                <span className="aspect-def-summary__desc" data-datum="compose.r-desc" data-kind="text" title={meta.desc}>
                  {meta.desc}
                </span>
              )}
            </>
          ) : undefined
        }
        meta={
          <span className="aspect-def-summary__meta" data-testid="aspect-def-summary-meta" ref={toggleWrapperRef}>
            {metaChips}
          </span>
        }
        trailing={trailing ? <div className="aspect-def-summary__actions">{trailing}</div> : undefined}
      />
      {(hasNeeds || meta?.targets || meta?.wiredTo || meta?.answeredByFamily || meta?.unresolved || meta?.noNeeds) && (
        <div className="aspect-def-summary__needs" data-container="chips" data-testid="aspect-def-summary-needs">
          {needs?.map((need) => (
            <span
              key={need.key}
              className="aspect-def-summary__need"
              data-container="chips > each(needs)"
              data-datum="compose.x"
              data-kind="chip"
            >
              {need.key}: <b data-datum="compose.n-answer" data-kind="strong">{need.answer}</b>
              <span className="aspect-def-summary__need-chip" data-datum="compose.window-at-word-n-at" data-kind="chip">
                {need.atWord}
              </span>
            </span>
          ))}
          {meta?.targets && (
            <span className="aspect-def-summary__need" data-datum="compose.targets" data-kind="chip">
              {meta.targets.label} <b data-datum="compose.seeded-here" data-kind="strong">{meta.targets.value}</b>
              <span className="aspect-def-summary__need-chip" data-datum="compose.per-sphere" data-kind="chip">
                {meta.targets.atWord}
              </span>
            </span>
          )}
          {/* Contract order 3 — after the targets chip, before `answered-by-the-family`. */}
          {meta?.wiredTo && (
            <span className="aspect-def-summary__wired" data-datum="compose.wired-to" data-kind="chip">
              <i className="pi pi-sitemap" aria-hidden="true" />
              {meta.wiredTo}
            </span>
          )}
          {meta?.answeredByFamily && (
            <span className="aspect-def-summary__hint" data-datum="compose.answered-by-the-family" data-kind="hint">
              {meta.answeredByFamily}
            </span>
          )}
          {/* Contract order 5 — between `answered-by-the-family` and `no-needs`. */}
          {meta?.unresolved && (
            <span className="aspect-def-summary__unresolved" data-datum="compose.not-defined-yet-blocks-publish" data-kind="callout">
              <i className="pi pi-link" aria-hidden="true" />
              {meta.unresolved}
            </span>
          )}
          {meta?.noNeeds && (
            <span className="aspect-def-summary__hint" data-datum="compose.no-needs" data-kind="hint">
              {meta.noNeeds}
            </span>
          )}
        </div>
      )}
      {expanded && (
        <div
          ref={detailRef}
          className="aspect-def-summary__detail"
          data-testid="aspect-def-summary-detail"
          onKeyDown={handleDetailKeyDown}
        >
          {children}
        </div>
      )}
    </div>
  );
};

AspectDefSummaryMolecule.displayName = 'AspectDefSummaryMolecule';

export default AspectDefSummaryMolecule;
